import React, { useState } from 'react';
import { Play, Settings2, Users } from 'lucide-react';
import { LETTER_POS_LABELS, LetterPos, LetterRule, READING_MODE_LABELS, ReadingMode, RULE_PRESETS, ruleKey } from '../../../utils/initials';
import { GameSettings, SideId } from '../../../utils/initialsGame';
import { Btn, Field, inputClass, Panel, PanelTitle, Segmented } from './parts';

const POSITIONS: LetterPos[] = ['first', 'second', 'last'];

const posOptions = POSITIONS.map(p => ({ value: p, label: LETTER_POS_LABELS[p] }));

interface Props {
  initial: GameSettings;
  onStart: (settings: GameSettings) => void;
}

/** Sides, rules and timing for a new match. */
const InitialsSetup: React.FC<Props> = ({ initial, onStart }) => {
  const [settings, setSettings] = useState<GameSettings>(initial);
  const presetIds = RULE_PRESETS.map(p => ruleKey(p.rule));
  const [ruleChoice, setRuleChoice] = useState<string>(() =>
    initial.rule === 'random' ? 'random' : presetIds.includes(ruleKey(initial.rule)) ? ruleKey(initial.rule) : 'custom'
  );
  const [custom, setCustom] = useState<LetterRule>(initial.rule === 'random' ? { name: 'first', surname: 'first' } : initial.rule);

  const setSide = (side: SideId, field: 'name' | 'members', value: string) =>
    setSettings(s => ({ ...s, sides: { ...s.sides, [side]: { ...s.sides[side], [field]: value } } }));

  const chooseRule = (choice: string) => {
    setRuleChoice(choice);
    if (choice === 'random') setSettings(s => ({ ...s, rule: 'random' }));
    else if (choice === 'custom') setSettings(s => ({ ...s, rule: custom }));
    else setSettings(s => ({ ...s, rule: RULE_PRESETS.find(p => ruleKey(p.rule) === choice)!.rule }));
  };

  const setCustomPos = (part: keyof LetterRule, pos: LetterPos) => {
    const next = { ...custom, [part]: pos };
    setCustom(next);
    setSettings(s => ({ ...s, rule: next }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onStart(settings);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <Panel>
        <PanelTitle icon={<Users className="h-5 w-5 text-sky-300" />}>Tərəflər</PanelTitle>
        <div className="grid gap-4 p-4 sm:grid-cols-2 sm:p-5">
          {(['a', 'b'] as SideId[]).map((side, i) => (
            <div key={side} className="flex flex-col gap-3">
              <Field label={i === 0 ? 'Birinci tərəf' : 'İkinci tərəf'}>
                <input
                  className={inputClass}
                  value={settings.sides[side].name}
                  onChange={e => setSide(side, 'name', e.target.value)}
                  placeholder="Oyunçu və ya komanda adı"
                  maxLength={40}
                />
              </Field>
              <input
                className={inputClass}
                value={settings.sides[side].members}
                onChange={e => setSide(side, 'members', e.target.value)}
                placeholder="Komanda üzvləri (istəyə görə)"
                aria-label="Komanda üzvləri"
                maxLength={120}
              />
            </div>
          ))}
        </div>
      </Panel>

      <Panel>
        <PanelTitle icon={<Settings2 className="h-5 w-5 text-sky-300" />}>Qaydalar</PanelTitle>
        <div className="flex flex-col gap-5 p-4 sm:p-5">
          <Field label="Hərflər haradan götürülür">
            <Segmented
              value={ruleChoice}
              onChange={chooseRule}
              options={[
                ...RULE_PRESETS.map(p => ({ value: ruleKey(p.rule), label: p.title })),
                { value: 'random', label: 'Hər raund təsadüfi' },
                { value: 'custom', label: 'Özüm seçim' },
              ]}
            />
          </Field>
          {ruleChoice === 'custom' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Adın neçənci hərfi">
                <Segmented value={custom.name} onChange={pos => setCustomPos('name', pos)} options={posOptions} />
              </Field>
              <Field label="Soyadın neçənci hərfi">
                <Segmented value={custom.surname} onChange={pos => setCustomPos('surname', pos)} options={posOptions} />
              </Field>
            </div>
          )}

          <Field
            label="Hərfləri kim seçir"
            hint={
              settings.letterSource === 'auto'
                ? 'Platforma hər raund hərfləri özü seçir. İstəsəniz, raund başlamazdan əvvəl dəyişə bilərsiniz.'
                : 'Hər raundda bir tərəf adın, digəri soyadın hərfini seçir.'
            }
          >
            <Segmented<GameSettings['letterSource']>
              value={settings.letterSource}
              onChange={letterSource => setSettings(s => ({ ...s, letterSource }))}
              options={[
                { value: 'auto', label: 'Platforma' },
                { value: 'manual', label: 'Tərəflər özləri' },
              ]}
            />
          </Field>

          <Field label="Ad necə oxunur" hint={READING_MODE_LABELS[settings.mode].description}>
            <Segmented<ReadingMode>
              value={settings.mode}
              onChange={mode => setSettings(s => ({ ...s, mode }))}
              options={(Object.keys(READING_MODE_LABELS) as ReadingMode[]).map(m => ({ value: m, label: READING_MODE_LABELS[m].title }))}
            />
          </Field>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Hər gediş üçün vaxt">
              <Segmented
                value={settings.turnSeconds}
                onChange={turnSeconds => setSettings(s => ({ ...s, turnSeconds }))}
                options={[30, 45, 60, 90, 120].map(v => ({ value: v, label: `${v} san` }))}
              />
            </Field>
            <Field label="Oyun neçə raund qələbəyədək">
              <Segmented
                value={settings.targetWins}
                onChange={targetWins => setSettings(s => ({ ...s, targetWins }))}
                options={[...[3, 5, 7, 10].map(v => ({ value: v, label: String(v) })), { value: 0, label: 'Limitsiz' }]}
              />
            </Field>
          </div>
        </div>
      </Panel>

      <Panel className="p-4 text-sm leading-relaxed text-white/60 sm:p-5">
        <p className="mb-2 font-bold text-white/80">Necə oynanılır</p>
        <ul className="list-disc space-y-1 pl-5">
          <li>Hər raundda platforma iki hərf seçir: biri ad, digəri soyad üçün (istəsəniz, hərfləri özünüz də seçə bilərsiniz).</li>
          <li>Növbə ilə bu hərflərə uyğun real şəxs deyilir və onun kim olduğu qısaca yazılır.</li>
          <li>Sistem hərfləri və təkrarları yoxlayır, rəqib isə şəxsin real olduğunu təsdiqləyir və ya etiraz edir.</li>
          <li>Vaxtında tapa bilməyən növbəni itirir: rəqib əvvəl tapmışdısa raundu o qazanır, hələ tapmamışdısa bir şansı var. Hər iki tərəf ardıcıl tapa bilmirsə — heç-heçə.</li>
        </ul>
      </Panel>

      <Btn type="submit" className="self-end px-6 py-3 text-base">
        <Play className="h-4 w-4" />
        Oyuna başla
      </Btn>
    </form>
  );
};

export default InitialsSetup;
